import React from 'react';
import Button from '../../../components/ui/Button';
import { ChevronUp, ChevronDown } from 'lucide-react';

const SongTable = ({ songs, sortConfig, onSort, onView, onEdit }) => {
  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) {
      return null;
    }
    return sortConfig?.direction === 'asc' ? 
      <ChevronUp className="w-4 h-4 ml-1" /> : 
      <ChevronDown className="w-4 h-4 ml-1" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatEditor = (teamMember) => {
    return teamMember?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'Unknown';
  };

  if (!songs?.length) {
    return (
      <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg p-8 text-center">
        <p className="text-white text-lg">No songs found matching your search criteria.</p>
      </div>
    );
  }

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm shadow-lg overflow-hidden rounded-xl">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-white">Song Database</h3>
        <p className="mt-1 max-w-2xl text-sm text-blue-100">
          Comprehensive list of all songs in the database with sorting and management options.
        </p>
      </div>
      
      <div className="border-t border-white border-opacity-20">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white divide-opacity-20">
            <thead className="bg-black bg-opacity-20">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider cursor-pointer hover:bg-white hover:bg-opacity-10 transition-colors"
                  onClick={() => onSort('song_title')}
                >
                  <div className="flex items-center">
                    Song Title
                    {getSortIcon('song_title')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider cursor-pointer hover:bg-white hover:bg-opacity-10 transition-colors"
                  onClick={() => onSort('musical')}
                >
                  <div className="flex items-center">
                    Musical
                    {getSortIcon('musical')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider cursor-pointer hover:bg-white hover:bg-opacity-10 transition-colors"
                  onClick={() => onSort('editor')}
                >
                  <div className="flex items-center">
                    Editor
                    {getSortIcon('editor')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider cursor-pointer hover:bg-white hover:bg-opacity-10 transition-colors"
                  onClick={() => onSort('created_at')}
                >
                  <div className="flex items-center">
                    Date Added
                    {getSortIcon('created_at')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-blue-100 uppercase tracking-wider cursor-pointer hover:bg-white hover:bg-opacity-10 transition-colors"
                  onClick={() => onSort('updated_at')}
                >
                  <div className="flex items-center">
                    Last Edited
                    {getSortIcon('updated_at')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-blue-100 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white divide-opacity-20">
              {songs?.map((song) => (
                <tr key={song?.id} className="hover:bg-white hover:bg-opacity-10 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-white">
                      {song?.overview?.songTitle || 'Untitled Song'}
                    </div>
                    {song?.overview?.character && (
                      <div className="text-xs text-blue-200">
                        Character: {song?.overview?.character}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">
                      {song?.overview?.musical || 'Unknown Musical'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">
                      {formatEditor(song?.team_member)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-200">
                    {formatDate(song?.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-200">
                    {formatDate(song?.updated_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onView(song?.id)}
                        className="border-blue-300 text-blue-200 hover:bg-blue-400 hover:text-white rounded-lg"
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit(song?.id)}
                        className="border-green-300 text-green-200 hover:bg-green-400 hover:text-white rounded-lg"
                      >
                        Edit
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SongTable;